/* ==========================================================================
   Maintrade Collection  ·  assets/mt-collection.js
   <mt-collection> custom element:
   - filters / sort / pagination update the grid with the Section Rendering
     API (no full page reload) and keep the URL in sync (back button works)
   - filter drawer on mobile, dropdown panels on desktop
   - one-click quick add for single-variant products
   - fade-up reveal
   Without JavaScript every control still works as a normal link / GET form.
   ========================================================================== */

(function () {
  'use strict';

  if (!window.customElements || window.customElements.get('mt-collection')) return;

  var DESKTOP_QUERY = '(min-width: 990px)';
  var REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var CACHE_LIMIT = 30;

  function root() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function esc(value) {
    return window.CSS && CSS.escape ? CSS.escape(value) : String(value).replace(/["\\]/g, '\\$&');
  }

  class MtCollection extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.cache = new Map();
      this.controller = null;
      this.requestId = 0;
      this.timer = null;
      this.lastFocus = null;
      this.mq = window.matchMedia(DESKTOP_QUERY);

      this._onClick = this.onClick.bind(this);
      this._onChange = this.onChange.bind(this);
      this._onSubmit = this.onSubmit.bind(this);
      this._onKeydown = this.onKeydown.bind(this);
      this._onToggle = this.onToggle.bind(this);
      this._onDocClick = this.onDocClick.bind(this);
      this._onPop = this.onPopState.bind(this);
      this._onMq = this.onBreakpoint.bind(this);

      this.addEventListener('click', this._onClick);
      this.addEventListener('change', this._onChange);
      this.addEventListener('submit', this._onSubmit);
      this.addEventListener('keydown', this._onKeydown);
      this.addEventListener('toggle', this._onToggle, true);
      document.addEventListener('click', this._onDocClick);
      window.addEventListener('popstate', this._onPop);
      if (this.mq.addEventListener) this.mq.addEventListener('change', this._onMq);
      else if (this.mq.addListener) this.mq.addListener(this._onMq);

      var openBtn = this.querySelector('[data-open-drawer]');
      if (openBtn) openBtn.setAttribute('aria-expanded', 'false');

      this.syncDialogRole();
      this.initReveal();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this._onClick);
      this.removeEventListener('change', this._onChange);
      this.removeEventListener('submit', this._onSubmit);
      this.removeEventListener('keydown', this._onKeydown);
      this.removeEventListener('toggle', this._onToggle, true);
      document.removeEventListener('click', this._onDocClick);
      window.removeEventListener('popstate', this._onPop);
      if (this.mq.removeEventListener) this.mq.removeEventListener('change', this._onMq);
      else if (this.mq.removeListener) this.mq.removeListener(this._onMq);
      document.documentElement.classList.remove('mt-col-lock');
      clearTimeout(this.timer);
      if (this.controller) this.controller.abort();
      if (this.io) this.io.disconnect();
    }

    /* ------------------------------------------------------------------
       Events
       ------------------------------------------------------------------ */

    onClick(event) {
      var target = event.target;

      var opener = target.closest('[data-open-drawer]');
      if (opener) {
        this.openDrawer(opener);
        return;
      }

      if (target.closest('[data-close-drawer]')) {
        this.closeDrawer();
        return;
      }

      var link = target.closest('a[data-nav]');
      if (link && !(event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button > 0)) {
        event.preventDefault();
        this.update(link.href, { scroll: link.hasAttribute('data-page-link') });
        return;
      }

      var add = target.closest('[data-quick-add]');
      if (add) {
        event.preventDefault();
        this.quickAdd(add);
      }
    }

    onChange(event) {
      var field = event.target;
      if (!field.closest || !field.closest('[data-form]')) return;

      if (field.type === 'number') this.sanitizePrice(field);

      clearTimeout(this.timer);
      this.timer = setTimeout(
        function () {
          this.update(this.buildUrl());
        }.bind(this),
        field.type === 'checkbox' ? 180 : 60
      );
    }

    onSubmit(event) {
      if (!event.target.matches('[data-form]')) return;
      event.preventDefault();
      clearTimeout(this.timer);
      this.update(this.buildUrl());
    }

    onKeydown(event) {
      if (event.key === 'Escape') {
        if (this.isDrawerOpen()) {
          this.closeDrawer();
          return;
        }
        var open = this.querySelector('details.mt-col__filter[open]');
        if (open && this.mq.matches) {
          open.open = false;
          var summary = open.querySelector('summary');
          if (summary) summary.focus();
        }
        return;
      }

      if (event.key === 'Tab' && this.isDrawerOpen()) this.trapFocus(event);
    }

    onToggle(event) {
      var details = event.target;
      if (!(details instanceof HTMLDetailsElement) || !details.open || !this.mq.matches) return;
      this.querySelectorAll('details.mt-col__filter[open]').forEach(function (other) {
        if (other !== details) other.open = false;
      });
    }

    onDocClick(event) {
      if (!this.mq.matches) return;
      if (event.target.closest && event.target.closest('.mt-col__filter')) return;
      this.querySelectorAll('details.mt-col__filter[open]').forEach(function (d) {
        d.open = false;
      });
    }

    onPopState() {
      this.update(window.location.href, { push: false });
    }

    onBreakpoint() {
      if (this.mq.matches) this.closeDrawer(true);
      this.syncDialogRole();
    }

    /* ------------------------------------------------------------------
       Drawer
       ------------------------------------------------------------------ */

    isDrawerOpen() {
      var drawer = this.querySelector('[data-drawer]');
      return !!drawer && drawer.classList.contains('is-open');
    }

    syncDialogRole() {
      var drawer = this.querySelector('[data-drawer]');
      if (!drawer) return;
      if (this.mq.matches) {
        drawer.removeAttribute('role');
        drawer.removeAttribute('aria-modal');
      } else {
        drawer.setAttribute('role', 'dialog');
        drawer.setAttribute('aria-modal', 'true');
      }
    }

    openDrawer(button) {
      var drawer = this.querySelector('[data-drawer]');
      var overlay = this.querySelector('.mt-col__overlay');
      if (!drawer) return;

      this.lastFocus = button || document.activeElement;
      drawer.classList.add('is-open');
      if (overlay) overlay.classList.add('is-open');
      document.documentElement.classList.add('mt-col-lock');
      if (button) button.setAttribute('aria-expanded', 'true');

      setTimeout(function () {
        var close = drawer.querySelector('.mt-col__close');
        if (close) close.focus();
      }, 60);
    }

    closeDrawer(skipFocus) {
      var drawer = this.querySelector('[data-drawer]');
      var overlay = this.querySelector('.mt-col__overlay');
      if (!drawer || !drawer.classList.contains('is-open')) return;

      drawer.classList.remove('is-open');
      if (overlay) overlay.classList.remove('is-open');
      document.documentElement.classList.remove('mt-col-lock');

      var opener = this.querySelector('[data-open-drawer]');
      if (opener) opener.setAttribute('aria-expanded', 'false');
      if (!skipFocus && this.lastFocus && this.lastFocus.focus) this.lastFocus.focus();
    }

    trapFocus(event) {
      var drawer = this.querySelector('[data-drawer]');
      var nodes = drawer.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select, summary'
      );
      if (!nodes.length) return;
      var first = nodes[0];
      var last = nodes[nodes.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    /* ------------------------------------------------------------------
       Filtering, sorting, pagination
       ------------------------------------------------------------------ */

    sanitizePrice(input) {
      if (input.value !== '' && Number(input.value) < 0) input.value = '0';

      var form = input.closest('[data-form]');
      var fields = form.querySelectorAll('input[type="number"]');
      if (fields.length < 2) return;
      var min = fields[0];
      var max = fields[1];
      if (min.value !== '' && max.value !== '' && Number(min.value) > Number(max.value)) {
        var swap = min.value;
        min.value = max.value;
        max.value = swap;
      }
    }

    buildUrl() {
      var form = this.querySelector('[data-form]');
      var params = new URLSearchParams();
      new FormData(form).forEach(function (value, key) {
        if (value !== '') params.append(key, value);
      });
      var query = params.toString();
      return form.getAttribute('action') + (query ? '?' + query : '');
    }

    setLoading(state) {
      this.classList.toggle('is-loading', state);
      this.setAttribute('aria-busy', state ? 'true' : 'false');
    }

    async fetchSection(target) {
      var request = new URL(target.href);
      request.searchParams.set('section_id', this.sectionId);
      var key = request.toString();

      if (this.cache.has(key)) return this.cache.get(key);

      if (this.controller) this.controller.abort();
      this.controller = new AbortController();

      var response = await fetch(key, { signal: this.controller.signal });
      if (!response.ok) throw new Error('Request failed: ' + response.status);
      var html = await response.text();

      if (this.cache.size >= CACHE_LIMIT) this.cache.delete(this.cache.keys().next().value);
      this.cache.set(key, html);
      return html;
    }

    async update(url, options) {
      var settings = options || {};
      var push = settings.push !== false;
      var target = new URL(url, window.location.origin);
      var id = ++this.requestId;

      this.setLoading(true);

      try {
        var html = await this.fetchSection(target);
        if (id !== this.requestId) return;

        this.render(html);

        if (push) {
          window.history.pushState({ mtCol: true }, '', target.pathname + target.search);
        }
        if (settings.scroll) this.scrollToTop();
      } catch (error) {
        if (error && error.name === 'AbortError') return;
        window.location.href = target.href;
        return;
      }

      if (id === this.requestId) this.setLoading(false);
    }

    render(html) {
      var doc = new DOMParser().parseFromString(html, 'text/html');
      var next = doc.getElementById(this.id) || doc.querySelector('mt-collection');
      if (!next) throw new Error('Section not found in response');

      // results
      var results = this.querySelector('[data-results]');
      var nextResults = next.querySelector('[data-results]');
      if (results && nextResults) results.innerHTML = nextResults.innerHTML;

      // active filter chips
      var chips = this.querySelector('[data-chips]');
      var nextChips = next.querySelector('[data-chips]');
      if (chips && nextChips) chips.replaceWith(nextChips);

      // product count
      var count = this.querySelector('[data-count]');
      var nextCount = next.querySelector('[data-count]');
      if (count && nextCount) count.textContent = nextCount.textContent;

      // number badge on the mobile Filter button
      var btnCount = this.querySelector('[data-filter-btn-count]');
      var nextBtnCount = next.querySelector('[data-filter-btn-count]');
      if (btnCount && nextBtnCount) btnCount.innerHTML = nextBtnCount.innerHTML;

      // sort select (needed when going back / forward)
      var sort = this.querySelector('select[name="sort_by"]');
      var nextSort = next.querySelector('select[name="sort_by"]');
      if (sort && nextSort) sort.value = nextSort.value;

      // clear-all link in the drawer
      var clear = this.querySelector('.mt-col__clear');
      var nextClear = next.querySelector('.mt-col__clear');
      if (clear && nextClear) clear.setAttribute('href', nextClear.getAttribute('href'));

      this.syncFilters(next);
      this.observeCards();
    }

    /* Update the filter inputs in place so open panels and focus survive */
    syncFilters(next) {
      var current = this.querySelector('[data-filters]');
      var incoming = next.querySelector('[data-filters]');
      if (!current || !incoming) return;

      var ids = function (scope) {
        return Array.prototype.map
          .call(scope.querySelectorAll('details[data-filter-id]'), function (d) {
            return d.dataset.filterId;
          })
          .join('|');
      };

      if (ids(current) !== ids(incoming)) {
        var open = new Set(
          Array.prototype.map.call(current.querySelectorAll('details[open]'), function (d) {
            return d.dataset.filterId;
          })
        );
        current.innerHTML = incoming.innerHTML;
        current.querySelectorAll('details[data-filter-id]').forEach(function (d) {
          if (open.has(d.dataset.filterId)) d.open = true;
        });
        return;
      }

      incoming.querySelectorAll('details[data-filter-id]').forEach(function (nextDetails) {
        var currentDetails = current.querySelector(
          'details[data-filter-id="' + esc(nextDetails.dataset.filterId) + '"]'
        );
        if (!currentDetails) return;

        var summary = currentDetails.querySelector('summary');
        var nextSummary = nextDetails.querySelector('summary');
        if (summary && nextSummary) summary.innerHTML = nextSummary.innerHTML;

        nextDetails.querySelectorAll('input').forEach(function (nextInput) {
          if (!nextInput.id) return;
          var input = currentDetails.querySelector('#' + esc(nextInput.id));
          if (!input) return;

          if (nextInput.type === 'checkbox') {
            input.checked = nextInput.checked;
            input.disabled = nextInput.disabled;
            var label = input.closest('label');
            var nextLabel = nextInput.closest('label');
            if (label && nextLabel) {
              label.classList.toggle('is-disabled', nextInput.disabled);
              var c = label.querySelector('.mt-col__check-count');
              var nc = nextLabel.querySelector('.mt-col__check-count');
              if (c && nc) c.textContent = nc.textContent;
            }
          } else if (document.activeElement !== input) {
            input.value = nextInput.value;
            input.placeholder = nextInput.placeholder;
          }
        });

        var hint = currentDetails.querySelector('.mt-col__hint');
        var nextHint = nextDetails.querySelector('.mt-col__hint');
        if (hint && nextHint) hint.textContent = nextHint.textContent;
      });
    }

    scrollToTop() {
      var anchor = this.querySelector('[data-toolbar]') || this;
      anchor.scrollIntoView({ behavior: REDUCED_MOTION ? 'auto' : 'smooth', block: 'start' });
    }

    /* ------------------------------------------------------------------
       Reveal
       ------------------------------------------------------------------ */

    initReveal() {
      var inEditor = window.Shopify && window.Shopify.designMode;
      if (this.dataset.reveal !== 'true' || REDUCED_MOTION || inEditor || !('IntersectionObserver' in window)) {
        return;
      }

      this.classList.add('is-reveal');
      this.io = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('is-in');
            this.io.unobserve(entry.target);
          }, this);
        }.bind(this),
        { rootMargin: '0px 0px -6% 0px', threshold: 0.05 }
      );
      this.observeCards();
    }

    observeCards() {
      if (!this.io) return;
      this.querySelectorAll('.mt-col__card:not(.is-in)').forEach(function (card) {
        this.io.observe(card);
      }, this);
    }

    /* ------------------------------------------------------------------
       Quick add
       ------------------------------------------------------------------ */

    async quickAdd(button) {
      if (button.hasAttribute('data-busy')) return;

      var idle = button.dataset.label || button.textContent;
      var reset = function (delay) {
        setTimeout(function () {
          button.textContent = idle;
          button.classList.remove('is-added', 'is-error');
        }, delay);
      };

      button.setAttribute('data-busy', '');
      button.textContent = button.dataset.labelAdding || 'Adding…';

      try {
        var response = await fetch(root() + 'cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: Number(button.dataset.variantId), quantity: 1 })
        });
        var data = await response.json();
        if (!response.ok) throw new Error(data.description || data.message || 'Could not add');

        this.cache.clear();
        button.textContent = button.dataset.labelAdded || 'Added';
        button.classList.add('is-added');
        reset(2200);
        this.refreshCart();
      } catch (error) {
        button.textContent = button.dataset.labelError || 'Try again';
        button.classList.add('is-error');
        reset(2600);
      } finally {
        button.removeAttribute('data-busy');
      }
    }

    async refreshCart() {
      try {
        var cart = await (await fetch(root() + 'cart.js', { headers: { Accept: 'application/json' } })).json();

        // Dawn-style themes
        if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS) {
          window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'mt-collection', cartData: cart });
        }

        // generic events other themes / apps can listen to
        document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { cart: cart }, bubbles: true }));
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: cart }, bubbles: true }));

        // refresh the header cart count bubble when the theme has one
        var bubble = document.getElementById('cart-icon-bubble');
        if (bubble) {
          var res = await fetch(root() + '?sections=cart-icon-bubble');
          var json = await res.json();
          var markup = json['cart-icon-bubble'];
          if (markup) {
            var inner = new DOMParser().parseFromString(markup, 'text/html').querySelector('.shopify-section');
            if (inner) bubble.innerHTML = inner.innerHTML;
          }
        }
      } catch (error) {
        /* the item is in the cart; a stale counter is not worth an error */
      }
    }
  }

  window.customElements.define('mt-collection', MtCollection);
})();